import React, { useState, useEffect } from 'react';
import { Box, Typography, TextField, Collapse } from '@mui/material';
import { Comment } from '@mui/icons-material';

interface CommentFieldProps {
  value?: string;
  onChange: (comment: string) => void;
  language: 'en' | 'no';
  disabled?: boolean;
  placeholder?: string;
  maxLength?: number;
}

export const CommentField: React.FC<CommentFieldProps> = ({
  value = '',
  onChange,
  language,
  disabled = false,
  placeholder,
  maxLength = 500,
}) => {
  const [localValue, setLocalValue] = useState<string>(value);
  const [isFocused, setIsFocused] = useState(false);

  // Update local value when prop value changes
  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const handleTextChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = event.target.value;

    // Enforce max length
    if (newValue.length <= maxLength) {
      setLocalValue(newValue);
      onChange(newValue);
    }
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  const getPlaceholderText = (): string => {
    if (placeholder) return placeholder;
    return language === 'no'
      ? 'Legg til en kommentar (valgfritt)...'
      : 'Add a comment (optional)...';
  };

  const getLabelText = (): string => {
    return language === 'no' ? 'Kommentar' : 'Comment';
  };

  const isOverLimit = localValue.length > maxLength;
  const showCharacterCount = isFocused || localValue.length > 0;

  return (
    <Box sx={{ width: '100%', mt: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Comment sx={{ fontSize: 16, color: 'text.secondary', mr: 1 }} />
        <Typography variant="caption" color="text.secondary">
          {getLabelText()}
        </Typography>
      </Box>

      <TextField
        multiline
        rows={3}
        fullWidth
        size="small"
        value={localValue}
        onChange={handleTextChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={getPlaceholderText()}
        disabled={disabled}
        error={isOverLimit}
        sx={{
          '& .MuiOutlinedInput-root': {
            backgroundColor: 'background.paper',
            '&:hover': {
              backgroundColor: 'action.hover',
            },
            '&.Mui-focused': {
              backgroundColor: 'background.paper',
            },
          },
        }}
        inputProps={{
          'aria-label': getLabelText(),
          'aria-describedby': 'comment-character-count',
          maxLength: maxLength + 10, // Allow slight overage for UX
        }}
        data-testid="comment-field"
      />

      <Collapse in={showCharacterCount}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
          <Box />
          <Typography
            id="comment-character-count"
            variant="caption"
            sx={{
              color: isOverLimit ? 'error.main' : 'text.secondary',
            }}
            data-testid="comment-character-count"
          >
            {localValue.length}/{maxLength}
          </Typography>
        </Box>
      </Collapse>

      {isOverLimit && (
        <Typography
          color="error"
          variant="caption"
          sx={{ display: 'block', mt: 0.5 }}
          role="alert"
          data-testid="comment-length-error"
        >
          {language === 'no'
            ? `Kommentaren er ${localValue.length - maxLength} tegn for lang`
            : `Comment is ${localValue.length - maxLength} characters too long`}
        </Typography>
      )}
    </Box>
  );
};
